<template>
    <Head title="Realtime Role Play Practice" />
    <div class="relative h-dvh w-full flex flex-col items-center justify-center background">
        <!-- Top bar: Controls/Settings (left) + Tips/End Session (right) in a single flex row -->
        <div class="absolute inset-x-0 top-0 px-4 pt-4 z-10 flex items-start justify-between gap-2">
            <!-- Left: Controls + Settings stacked -->
            <div class="flex flex-col items-start gap-2 min-w-0">
                <!-- Controls bar: mute + style -->
                <div v-if="mute_enabled || style_adjustments_enabled" role="toolbar" aria-label="Session controls" class="rounded-2xl bg-white/90 backdrop-blur-sm shadow-lg">
                    <!-- Collapsed state -->
                    <button
                        v-if="controlsCollapsed"
                        type="button"
                        aria-label="Expand controls"
                        class="flex items-center gap-1.5 px-3 py-2 hover:bg-white rounded-2xl transition-colors text-valence-grey-500"
                        @click="controlsCollapsed = false">
                        <i class="bi bi-mic-fill text-sm" aria-hidden="true"></i>
                        <span class="details font-medium">Controls</span>
                        <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                    </button>
                    <!-- Expanded state -->
                    <div v-else class="flex items-center gap-1.5 px-2 py-1.5 sm:gap-2 sm:px-3 sm:py-2">
                        <BaseTooltip v-if="mute_enabled">
                            <template #trigger>
                                <button
                                    type="button"
                                    :aria-label="isMuted ? 'Unmute microphone' : 'Mute microphone'"
                                    :disabled="startupMicLocked"
                                    :aria-disabled="startupMicLocked"
                                    class="flex items-center justify-center rounded-full w-8 h-8 sm:w-10 sm:h-10 transition-colors"
                                    :class="[
                                        startupMicLocked ? 'opacity-50 cursor-not-allowed' : '',
                                        isMuted ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'hover:bg-valence-grey-100 text-valence-grey-800',
                                    ]"
                                    @click="toggleMute">
                                    <i :class="['bi text-base sm:text-lg', isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill']" aria-hidden="true"></i>
                                </button>
                            </template>
                            <template #content>{{ startupMicLocked ? "Mic locked while Nadia starts" : isMuted ? "Unmute mic" : "Mute mic" }}</template>
                        </BaseTooltip>
                        <BaseTooltip v-if="mute_enabled">
                            <template #trigger>
                                <button
                                    type="button"
                                    :aria-label="isOutputMuted ? 'Turn on audio' : 'Turn off audio'"
                                    class="flex items-center justify-center rounded-full w-8 h-8 sm:w-10 sm:h-10 transition-colors"
                                    :class="isOutputMuted ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'hover:bg-valence-grey-100 text-valence-grey-800'"
                                    @click="toggleOutputAudio">
                                    <i :class="['bi text-base sm:text-lg', isOutputMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill']" aria-hidden="true"></i>
                                </button>
                            </template>
                            <template #content>{{ isOutputMuted ? "Unmute audio" : "Mute audio" }}</template>
                        </BaseTooltip>
                        <BasePopover v-if="style_adjustments_enabled" v-model:open="isStyleMenuOpen" side="bottom" :side-offset="8">
                            <template #trigger>
                                <button
                                    type="button"
                                    class="flex items-center gap-1.5 sm:gap-2 rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 body-small transition-colors hover:bg-valence-grey-100 text-valence-grey-800 min-h-9 sm:min-h-[44px] text-left">
                                    <div class="flex flex-col">
                                        <span class="details font-semibold text-valence-grey-600">Style:</span>
                                        <span class="body-small text-valence-grey-800 line-clamp-1">{{ currentStyleLabel }}</span>
                                    </div>
                                    <i :class="['bi text-sm text-valence-grey-400', isStyleMenuOpen ? 'bi-chevron-up' : 'bi-chevron-down']" aria-hidden="true"></i>
                                </button>
                            </template>
                            <template #content>
                                <div class="p-2 min-w-[220px]">
                                    <button
                                        v-for="option of role_play_adjustments"
                                        :key="option[0]"
                                        type="button"
                                        class="flex items-center justify-between w-full rounded-lg px-3 py-2.5 body-small transition-all hover:bg-valence-grey-50 min-h-[44px]"
                                        :class="[currentStyle === option[0] ? 'text-valence-primary-500 font-semibold' : 'text-valence-grey-800']"
                                        @click="handleStyleChange(option[0])">
                                        {{ option[1] }}
                                        <i v-if="currentStyle === option[0]" class="bi bi-check-lg text-valence-primary-500" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </template>
                        </BasePopover>
                        <button
                            type="button"
                            aria-label="Collapse controls"
                            class="flex items-center justify-center rounded-full w-7 h-7 sm:w-8 sm:h-8 hover:bg-valence-grey-100 text-valence-grey-400 transition-colors"
                            @click="controlsCollapsed = true">
                            <i class="bi bi-chevron-up text-sm" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>

                <!-- Settings bar (separate, collapsed by default) -->
                <div class="rounded-2xl bg-white/90 backdrop-blur-sm shadow-lg">
                    <!-- Collapsed state -->
                    <button
                        v-if="settingsCollapsed"
                        type="button"
                        aria-label="Expand settings"
                        class="flex items-center gap-1.5 px-3 py-2 hover:bg-white rounded-2xl transition-colors text-valence-grey-500"
                        @click="settingsCollapsed = false">
                        <i class="bi bi-gear text-sm" aria-hidden="true"></i>
                        <span class="details font-medium">Settings</span>
                        <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                    </button>
                    <!-- Expanded state -->
                    <div v-else class="px-1 py-1">
                        <div class="flex items-center justify-between px-2 py-0.5 sm:py-1">
                            <span class="details font-semibold text-valence-grey-600">Settings</span>
                            <button
                                type="button"
                                aria-label="Collapse settings"
                                class="flex items-center justify-center rounded-full w-6 h-6 sm:w-7 sm:h-7 hover:bg-valence-grey-100 text-valence-grey-400 transition-colors"
                                @click="settingsCollapsed = true">
                                <i class="bi bi-chevron-up text-xs" aria-hidden="true"></i>
                            </button>
                        </div>
                        <RealtimeSessionSettings :current-settings="session.currentSettings.value" @update="handleSettingsUpdate" />
                    </div>
                </div>
            </div>
            <!-- end left column -->

            <!-- Right: Tips (left on desktop, below on mobile) + End Session -->
            <div class="flex flex-col-reverse sm:flex-row items-end gap-2 shrink-0">
                <button
                    v-if="tipsCollapsed"
                    type="button"
                    aria-label="Show tips"
                    class="flex items-center gap-1.5 rounded-2xl bg-white/90 backdrop-blur-sm px-3 py-1.5 shadow-md hover:bg-white transition-colors"
                    @click="tipsCollapsed = false">
                    <i class="bi bi-info-circle text-valence-grey-500" aria-hidden="true"></i>
                    <span class="details font-medium text-valence-grey-500">Tips</span>
                </button>
                <BaseButton theme="primary" data-testid="end-session-button" @click="handleEndRolePlay">
                    <span class="sm:hidden">End Session</span>
                    <span class="hidden sm:inline">End Session & View Recap</span>
                </BaseButton>
            </div>
        </div>

        <!-- Tips panel (drops below the top bar, right-aligned) -->
        <div v-if="!tipsCollapsed" class="absolute right-4 top-16 z-10 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg px-4 py-3 max-w-xs">
            <div class="flex items-center justify-between mb-2">
                <span class="details font-semibold text-valence-grey-700">Tips & Troubleshooting</span>
                <button type="button" aria-label="Hide tips" class="text-valence-grey-400 hover:text-valence-grey-600" @click="tipsCollapsed = true">
                    <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                </button>
            </div>
            <ul class="space-y-1.5">
                <li class="details text-valence-grey-600 flex items-start gap-2">
                    <i class="bi bi-mic-fill text-xs text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                    <span><strong>Controls</strong> — mute your mic or audio anytime</span>
                </li>
                <li class="details text-valence-grey-600 flex items-start gap-2">
                    <i class="bi bi-headphones text-xs text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                    <span><strong>Noise reduction</strong> — background noise? try "Laptop" filtering</span>
                </li>
                <li class="details text-valence-grey-600 flex items-start gap-2">
                    <i class="bi bi-hand-index text-xs text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                    <span><strong>Interruptions</strong> — enable to talk over Nadia and cut in; disable if background noise is interrupting her</span>
                </li>
            </ul>
        </div>

        <!-- Transcript overlay: orb stays centered, transcript panel is absolute-positioned to the right -->
        <TranscriptOverlay
            :transcripts="transcripts"
            :status-text="overlayStatusText"
            :frozen="session.phase.value.stage === 'reconnecting'"
            :hidden="transcriptHidden"
            @toggle-hidden="transcriptHidden = !transcriptHidden">
            <RealTimeChatMarble class="w-48 sm:w-56 md:w-72" :level="audioLevel" :state="marbleState" />
        </TranscriptOverlay>

        <!-- Startup disclaimer: shown until either participant starts speaking -->
        <Transition name="fade">
            <div v-if="showDisclaimer" class="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                <div class="pointer-events-auto rounded-2xl bg-white/90 backdrop-blur-md shadow-lg px-6 py-5 max-w-sm mx-4 text-center">
                    <h3 class="body-small font-semibold text-valence-grey-800 mb-3">Voice coaching session</h3>
                    <ul class="text-left space-y-2 mb-4">
                        <li class="details text-valence-grey-700 flex items-start gap-2">
                            <i class="bi bi-pause-circle text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                            <span>Pause when you've finished a thought — Nadia will respond</span>
                        </li>
                        <li class="details text-valence-grey-700 flex items-start gap-2">
                            <i class="bi bi-hourglass-split text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                            <span>Brief silences are normal while processing</span>
                        </li>
                        <li class="details text-valence-grey-700 flex items-start gap-2">
                            <i class="bi bi-gear text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                            <span>Adjust <strong>Settings</strong> (top left) for mic and pace</span>
                        </li>
                        <li class="details text-valence-grey-700 flex items-start gap-2">
                            <i class="bi bi-info-circle text-valence-grey-400 mt-0.5 shrink-0" aria-hidden="true"></i>
                            <span>See <strong>Tips</strong> for troubleshooting anytime</span>
                        </li>
                    </ul>
                    <p class="details text-valence-grey-500 italic">Starting shortly…</p>
                </div>
            </div>
        </Transition>

        <!-- Bottom center: notifications + muted indicator -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-lg flex flex-col items-center gap-2 z-10">
            <!-- Notifications -->
            <Transition name="slide-down">
                <BaseNotification
                    v-if="showTimeWarning"
                    key="time-warning"
                    theme="error"
                    text="Your session will end in 5 minutes"
                    class="w-full"
                    @dismiss="showTimeWarning = false" />

                <BaseNotification v-else-if="session.phase.value.stage === 'reconnecting'" key="reconnecting" theme="error" class="w-full" :hide-dismiss="true">
                    <template #content>
                        <div class="flex flex-col gap-2">
                            <p class="body-small font-semibold">{{ statusText }}</p>
                            <p v-if="reconnectCountdown > 0" class="details">{{ reconnectCountdownText }}</p>
                            <p class="details">{{ CONFIG.reconnectionText.preserved }}</p>
                            <button type="button" class="body-small underline text-left mt-1" @click="handleEndRolePlay">End Session & View Recap</button>
                        </div>
                    </template>
                </BaseNotification>

                <BaseNotification v-else-if="session.phase.value.stage === 'reconnect_failed'" key="reconnect-failed" theme="error" class="w-full" :hide-dismiss="true">
                    <template #content>
                        <div class="flex flex-col gap-2">
                            <p class="body-small font-semibold">{{ CONFIG.reconnectionText.gaveUp }}</p>
                            <p class="details">{{ CONFIG.reconnectionText.gaveUpDetail }}</p>
                            <div class="flex gap-3 mt-2">
                                <button type="button" class="body-small underline text-left" @click="handleRejoinSession">Rejoin Session</button>
                                <button type="button" class="body-small underline text-left" @click="handleEndRolePlay">View Recap</button>
                            </div>
                        </div>
                    </template>
                </BaseNotification>

                <BaseNotification
                    v-else-if="showReconnected"
                    key="reconnected"
                    theme="success"
                    :text="CONFIG.reconnectionText.reconnected"
                    class="w-full"
                    @dismiss="showReconnected = false" />

                <BaseNotification
                    v-else-if="showInstabilityWarning"
                    key="instability"
                    theme="error"
                    :text="CONFIG.degradedText.connectionUnstable"
                    class="w-full"
                    @dismiss="showInstabilityWarning = false" />

                <BaseNotification v-else-if="error" theme="error" :text="error" class="w-full" @dismiss="error = null" />
            </Transition>

            <!-- Muted indicator -->
            <Transition name="slide-down">
                <div v-if="isMuted" class="text-center">
                    <p class="body-small font-semibold text-valence-grey-800">{{ mutedIndicatorTitle }}</p>
                    <p v-if="!startupMicLocked" class="details text-valence-grey-600">Nadia can't hear you right now</p>
                </div>
            </Transition>

            <!-- Audio muted indicator -->
            <Transition name="slide-down">
                <div v-if="isOutputMuted" class="text-center">
                    <p class="body-small font-semibold text-valence-grey-800">Audio Muted</p>
                    <p class="details text-valence-grey-600">You won't hear Nadia right now</p>
                </div>
            </Transition>

            <Disclaimer :message="disclaimer_message" />
        </div>
    </div>

    <!-- Guardrail Violation Modal -->
    <BaseDialog v-model:open="showGuardrailModal" :disable-close-on-backdrop-click="true" :show-close="false" title="Coaching boundary reached">
        <!-- On mobile the BaseDialog IS the bottom sheet (full-width, bg-white, overflow-y-auto).
             On md+ it renders as a centered card so we add border/shadow/rounded there. -->
        <div class="flex flex-col w-full min-h-[50dvh] md:min-h-0 md:max-w-[500px] md:rounded-xl md:border-2 md:border-[#F5F5F5] md:shadow-lg">
            <!-- Scrollable text — vertical only; flex-1 pushes button to the bottom when content is short -->
            <div class="flex flex-col flex-1 items-center text-center gap-4 px-6 pt-10 pb-6 overflow-x-hidden">
                <div class="flex flex-col gap-2 w-full">
                    <h3 class="title-subsection">
                        {{ guardrailSpokenRedirect ? "Staying within coaching guidelines" : "This conversation can't continue" }}
                    </h3>
                    <MarkdownText :text="guardrailMessage" class="body-small text-valence-grey-750" />
                </div>
            </div>
            <!-- Button bar — sticky to the bottom of the scroll container -->
            <div class="sticky bottom-0 w-full flex flex-col items-center gap-3 px-6 py-4 bg-white border-t border-[#F5F5F5]">
                <template v-if="guardrailSpokenRedirect">
                    <BaseButton class="w-full" theme="primary" @click="showGuardrailModal = false"> Continue </BaseButton>
                </template>
                <template v-else>
                    <BaseButton v-if="practice_again_params" class="w-full" theme="primary" @click="handleGuardrailRestart"> Start Over </BaseButton>
                    <BaseButton class="w-full" theme="secondary" @click="handleGuardrailEnd"> View Session Recap </BaseButton>
                </template>
            </div>
        </div>
    </BaseDialog>

    <!-- Guardrail Toast (spoken-redirect only — session continues) -->
    <Transition name="slide-up">
        <div v-if="showGuardrailToast" class="absolute inset-x-0 bottom-16 flex justify-center px-4 z-10">
            <!-- Customized org guardrail: clickable content + a separate dismiss button (can't nest
                 buttons, so the strip is a div with its own interactive children). -->
            <div
                v-if="guardrailToastCustomized"
                class="flex items-center gap-3 rounded-xl pl-4 pr-3 py-3 border border-valence-grey-250 bg-[#FAFAFA] text-valence-grey-850 shadow-md max-w-sm w-full">
                <button type="button" class="flex items-center gap-3 grow text-left hover:opacity-80" @click="openGuardrailModalFromToast">
                    <i class="bi bi-info-circle text-lg shrink-0 text-valence-grey-600" aria-hidden="true"></i>
                    <p class="body-small grow">Nadia kept the conversation aligned with coaching guidelines. <span class="underline">View details</span></p>
                </button>
                <button
                    type="button"
                    aria-label="Dismiss"
                    class="bi bi-x text-xl leading-none text-valence-grey-500 hover:text-valence-grey-800 shrink-0"
                    @click="dismissGuardrailToast"></button>
            </div>
            <!-- Core guardrail: info-only, no interaction on the strip itself -->
            <div v-else class="flex items-center gap-3 rounded-xl px-4 py-3 border border-valence-grey-250 bg-[#FAFAFA] text-valence-grey-850 shadow-md max-w-sm w-full">
                <i class="bi bi-info-circle text-lg shrink-0 text-valence-grey-600" aria-hidden="true"></i>
                <p class="body-small grow">Nadia kept the conversation aligned with coaching guidelines.</p>
                <button
                    type="button"
                    aria-label="Dismiss"
                    class="bi bi-x text-xl leading-none text-valence-grey-500 hover:text-valence-grey-800 shrink-0"
                    @click="dismissGuardrailToast"></button>
            </div>
        </div>
    </Transition>

    <!-- Session End Dialog -->
    <BaseDialog v-model:open="showSessionEndDialog" :disable-close-on-backdrop-click="true" title="Live chat session ended">
        <div class="flex flex-col items-center text-center rounded-xl py-10 px-6 w-full md:max-w-[500px] gap-6 border-2 border-[#F5F5F5] shadow-lg bg-white">
            <div class="w-32 h-32">
                <RealTimeChatMarble class="w-full h-full" :level="0" state="listening" />
            </div>
            <h3 class="title-subsection">Your live chat session has ended</h3>
            <p class="body-small text-valence-grey-750">Thank you for your conversation! Your session has reached its time limit.</p>
            <div class="w-full flex flex-col items-center gap-4 mt-6">
                <BaseButton class="w-2/5" theme="primary" @click="handleSessionEndDialogClose"> Role Play Recap </BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup>
import { getCookie } from "/js/utils";
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import jwtToken from "~vue/api/token";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import RealTimeChatMarble from "~vue/components/RealTimeChatMarbleV2.vue";
import RealtimeSessionSettings from "~vue/components/RealtimeSessionSettings.vue";
import TranscriptOverlay from "~vue/components/TranscriptOverlay.vue";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { REALTIME_SESSION_CONFIG, useRealtimeSession } from "~vue/composables/useRealtimeSession";
import { useRealtimeVoice } from "~vue/composables/useRealtimeVoiceV2";
import Disclaimer from "~vue/Disclaimer.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import SocketClient from "~vue/SocketClient.js";
import { useSessionTimer } from "~vue/useSessionTimer.js";
import { createWebSocketErrorHandler, logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const {
    ws_url,
    role_play,
    cs_id,
    cs_uuid,
    mute_enabled,
    style_adjustments_enabled,
    role_play_adjustments,
    role_play_personality,
    coach_api_realtime_enabled,
    practice_again_params,
} = defineProps({
    ws_url: { type: String, required: true },
    role_play: { type: Object, required: true },
    cs_id: { type: String, required: true },
    cs_uuid: { type: String, default: null },
    disclaimer_message: { type: String, default: "" },
    mute_enabled: { type: Boolean, default: false },
    style_adjustments_enabled: { type: Boolean, default: false },
    role_play_adjustments: { type: Array, default: () => [] },
    role_play_personality: { type: String, default: "" },
    coach_api_realtime_enabled: { type: Boolean, default: false },
    practice_again_params: { type: Object, default: null },
});

const STARTUP_MIC_LOCK_MS = 4_000;

const sendMessage = (buffer) => {
    const ws = socketClient.value?.instance;
    if (ws?.readyState === WebSocket.OPEN) {
        ws.send(buffer);
    }
};

const sendJsonMessage = (data) => {
    const ws = socketClient.value?.instance;
    if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
    }
};

const {
    startChat,
    endChat,
    muteChat,
    unmuteChat,
    audioLevel,
    audioQueue,
    isPlaying,
    isMuted,
    isOutputMuted,
    scheduleNextChunks,
    stopPlayback,
    muteOutputAudio,
    unmuteOutputAudio,
} = useRealtimeVoice({
    onMessageCallback: sendMessage,
});

// Shorthand for config references in template and script
const CONFIG = REALTIME_SESSION_CONFIG;

// Session state machine — receives semantic protocol events from the server
const session = useRealtimeSession();
const { transcripts, statusText } = session;
const overlayStatusText = computed(() => (isMuted.value || isOutputMuted.value ? "" : statusText.value));
const mutedIndicatorTitle = computed(() => (startupMicLocked.value ? "Initializing microphone..." : "Microphone Muted"));
const marbleState = computed(() => {
    if (session.phase.value.stage === "reconnecting" || session.phase.value.stage === "reconnect_failed") {
        return session.marbleState.value;
    }
    return isMuted.value ? "muted" : session.marbleState.value;
});

// Session end dialog state
const showSessionEndDialog = ref(false);

// Guardrail violation modal state
const showGuardrailModal = ref(false);
const guardrailMessage = ref("");
// True when the triggering guardrail_triggered event carried spoken_redirect:true — the
// model is already speaking the redirect, so the session should continue naturally.
const guardrailSpokenRedirect = ref(false);

// Guardrail toast state (spoken-redirect path only — session keeps going)
const showGuardrailToast = ref(false);
// True when the violated guardrail is an org customization — toast is persistent + clickable.
const guardrailToastCustomized = ref(false);
let guardrailToastTimer = null;
// response_id of a response we barged in on: its buffered audio is flushed and any
// still-in-flight deltas are dropped so the steered redirect (a new response_id) plays promptly.
let guardrailCutResponseId = null;

function clearGuardrailToastTimer() {
    if (guardrailToastTimer !== null) {
        clearTimeout(guardrailToastTimer);
        guardrailToastTimer = null;
    }
}

function dismissGuardrailToast() {
    clearGuardrailToastTimer();
    showGuardrailToast.value = false;
}

function openGuardrailModalFromToast() {
    dismissGuardrailToast();
    showGuardrailModal.value = true;
}

// Time warning state
const showTimeWarning = ref(false);

// Session timer - shows warning 5 minutes before session ends.
// Timer runs continuously (even while muted) since the OpenAI session clock keeps ticking.
const { stopTimer, startTimer } = useSessionTimer(
    25 * 60 * 1000, // 25 minutes (5 minutes before 30-minute session ends)
    () => {
        showTimeWarning.value = true;
    },
);

// Style adjustment state
const isStyleMenuOpen = ref(false);
const currentStyle = ref("IS_OK");

const currentStyleLabel = computed(() => {
    if (currentStyle.value === "IS_OK") {
        return role_play_personality || "As described in scenario";
    }
    const match = role_play_adjustments.find((opt) => opt[0] === currentStyle.value);
    return match ? match[1] : "Adjust style";
});

// Tracks the window between response.created and response.done (used for jitter buffer flush timing)
const isProcessingResponse = ref(false);

// Whether the user explicitly tapped mute (vs auto-mute from interruptions-off logic)
const userManuallyMuted = ref(false);
const startupMicLocked = ref(false);
let startupMicLockTimer = null;

function clearStartupMicLockTimer() {
    if (startupMicLockTimer !== null) {
        clearTimeout(startupMicLockTimer);
        startupMicLockTimer = null;
    }
}

function endStartupMicLock({ restoreMic = true } = {}) {
    clearStartupMicLockTimer();
    startupMicLocked.value = false;

    if (!restoreMic) {
        return;
    }

    if (!session.currentSettings.value.allowInterruptions && isPlaying.value) {
        return;
    }

    unmuteChat();
    session.setMuted(false);
}

function startStartupMicLock() {
    clearStartupMicLockTimer();
    startupMicLocked.value = true;
    userManuallyMuted.value = false;
    muteChat();
    session.setMuted(true);

    startupMicLockTimer = setTimeout(() => {
        endStartupMicLock();
    }, STARTUP_MIC_LOCK_MS);
}

// Mute / Unmute — stops sending user audio but lets Nadia finish speaking
const toggleMute = () => {
    if (startupMicLocked.value) {
        return;
    }

    if (isMuted.value) {
        userManuallyMuted.value = false;
        unmuteChat();
        session.setMuted(false);
        sendJsonMessage({ type: "unmute" });
        logUserInteraction("realtime_roleplay_unmuted", {}, cs_uuid);
    } else {
        userManuallyMuted.value = true;
        muteChat();
        session.setMuted(true);
        sendJsonMessage({ type: "mute" });
        logUserInteraction("realtime_roleplay_muted", {}, cs_uuid);
    }
};

const toggleOutputAudio = () => {
    if (isOutputMuted.value) {
        unmuteOutputAudio();
        logUserInteraction("realtime_roleplay_audio_unmuted", {}, cs_uuid);
    } else {
        muteOutputAudio();
        logUserInteraction("realtime_roleplay_audio_muted", {}, cs_uuid);
    }
};

// Auto-mute mic while Nadia is speaking when interruptions are off.
// Prevents VAD from triggering on background noise during playback.
// Does not interfere with the user's manual mute toggle.
watch(isPlaying, (playing) => {
    if (session.currentSettings.value.allowInterruptions) {
        return;
    }
    if (userManuallyMuted.value) {
        return;
    } // user took control, don't override
    if (playing) {
        muteChat();
        session.setMuted(true);
    } else {
        unmuteChat();
        session.setMuted(false);
    }
});

// When interruptions are turned back ON while auto-mute is in effect, unmute immediately.
// Without this, the isPlaying watcher bails out early (allowInterruptions=true) and the
// mic stays muted until the next isPlaying change.
watch(
    () => session.currentSettings.value.allowInterruptions,
    (allowInterruptions) => {
        if (allowInterruptions && !userManuallyMuted.value && isMuted.value) {
            unmuteChat();
            session.setMuted(false);
        }
    },
);

// Style change
const handleStyleChange = (adjustment) => {
    currentStyle.value = adjustment;
    isStyleMenuOpen.value = false;
    sendJsonMessage({ type: "adjust_style", adjustment });
    logUserInteraction("realtime_roleplay_style_adjusted", { adjustment }, cs_uuid);
};

// Session settings change — send to server, which validates and forwards to OpenAI
const handleSettingsUpdate = (settings) => {
    sendJsonMessage({ type: "update_settings", settings });
    logUserInteraction("realtime_roleplay_settings_updated", settings, cs_uuid);
};

const handleSessionOverMaximumDuration = () => {
    endStartupMicLock({ restoreMic: false });
    showSessionEndDialog.value = true;
    userEndedSession.value = true;
    session.setEnded("max_duration");
    // Disconnect WebSocket and end chat
    socketClient.value?.instance?.close();
    stopTimer();
    endChat();
};

/** Rejoin after reconnection gave up — reset state and reconnect from scratch */
const handleRejoinSession = () => {
    reconnectAttempts.value = 0;
    clearCountdownInterval();
    session.reset(false);
    error.value = null;
    socketClient.value?.connect();
};

// Translate a mic/setup error into the appropriate user-facing message.
const handleChatInitError = (e) => {
    endStartupMicLock({ restoreMic: false });
    logError(`Error initializing chat: ${e}`);
    session.setEnded("mic_error");
    if (e instanceof DOMException) {
        // Differentiate mic errors for a helpful message
        if (e.name === "NotAllowedError") {
            error.value = CONFIG.errorText.micPermissionDenied;
        } else if (e.name === "NotFoundError") {
            error.value = CONFIG.errorText.micNotFound;
        } else {
            error.value = CONFIG.errorText.genericError;
        }
    } else if (e instanceof TypeError) {
        // navigator.mediaDevices undefined — not a secure context
        error.value = CONFIG.errorText.micPermissionDenied;
    } else {
        error.value = CONFIG.errorText.genericError;
    }
};

const { micConsentBlocks } = useMicConsent();

// Start the mic and open the WebSocket for the current session props. Gated here rather than in
// onMounted so the practice-again re-init below inherits the check too.
const initChat = async () => {
    if (micConsentBlocks(initChat, handleMicConsentDecline)) {
        return;
    }
    await startChat();
    startStartupMicLock();
    await initializeWebSocket();
};

// Voice-only page: declining leaves nothing usable here.
const handleMicConsentDecline = () => {
    globalThis.history.back();
};

onMounted(async () => {
    try {
        await initChat();
    } catch (e) {
        handleChatInitError(e);
    }
});

// Inertia reuses the same component instance when navigating between pages with the
// same component name (e.g. practice-again goes from /realtime_chat/ABC/ to /realtime_chat/DEF/).
// onMounted doesn't re-fire, so we watch ws_url (unique per session) and re-initialize.
watch(
    () => ws_url,
    async (_newUrl, oldUrl) => {
        if (!oldUrl) {
            return;
        } // skip initial setup — onMounted handles that

        clearCountdownInterval();
        endStartupMicLock({ restoreMic: false });
        socketClient.value = null;
        userEndedSession.value = false;
        error.value = null;
        showReconnected.value = false;
        reconnectAttempts.value = 0;
        showInstabilityWarning.value = false;
        showGuardrailModal.value = false;
        guardrailMessage.value = "";
        guardrailSpokenRedirect.value = false;
        guardrailCutResponseId = null;
        showDisclaimer.value = true;
        showTimeWarning.value = false;
        showSessionEndDialog.value = false;
        disconnectTimestamps.length = 0;
        session.reset();
        stopTimer();
        startTimer();

        try {
            await initChat();
        } catch (e) {
            handleChatInitError(e);
        }
    },
);

const error = ref(null);
const showReconnected = ref(false);
const reconnectAttempts = ref(0);
const socketClient = ref(null);
const userEndedSession = ref(false);
const reconnectCountdown = ref(0);
const showInstabilityWarning = ref(false);
// Keep secondary panels collapsed, but show primary controls by default.
const transcriptHidden = ref(true);
const controlsCollapsed = ref(false);
const settingsCollapsed = ref(true);
const tipsCollapsed = ref(true);
const showDisclaimer = ref(true);
let countdownInterval = null;
const disconnectTimestamps = [];

const reconnectCountdownText = computed(() => CONFIG.reconnectionText.tryingCountdown.replace("{seconds}", String(reconnectCountdown.value)));

function clearCountdownInterval() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
    reconnectCountdown.value = 0;
}

function startReconnectCountdown(intervalMs) {
    clearCountdownInterval();
    reconnectCountdown.value = Math.ceil(intervalMs / 1000);
    countdownInterval = setInterval(() => {
        reconnectCountdown.value--;
        if (reconnectCountdown.value <= 0) {
            clearCountdownInterval();
        }
    }, 1000);
}

// Track disconnect frequency to surface connection quality issues.
// Uses a simple sliding window — not persisted across page reloads.
function detectInstability() {
    const now = Date.now();
    disconnectTimestamps.push(now);
    const cutoff = now - CONFIG.reconnection.instabilityWindowMs;
    while (disconnectTimestamps.length > 0 && disconnectTimestamps[0] < cutoff) {
        disconnectTimestamps.shift();
    }
    if (disconnectTimestamps.length >= CONFIG.reconnection.instabilityDisconnectThreshold) {
        showInstabilityWarning.value = true;
    }
}

function computeBackoffMs(attempt) {
    return Math.min(CONFIG.reconnection.baseIntervalMs * CONFIG.reconnection.backoffMultiplier ** (attempt - 1), CONFIG.reconnection.maxIntervalMs);
}

// Initialize WebSocket connection
const initializeWebSocket = async () => {
    // coach_api WebSocket endpoints require a JWT bearer subprotocol because
    // browsers cannot set Authorization headers on WebSocket upgrades.
    let subprotocols;
    if (coach_api_realtime_enabled) {
        try {
            await jwtToken.getToken();
        } catch {
            // Token fetch failure is non-fatal; connect() will surface a 1008 if auth fails.
        }
        if (jwtToken.apiToken.value) {
            // Use a getter so reconnects always pick up the latest token value.
            subprotocols = () => (jwtToken.apiToken.value ? ["bearer", jwtToken.apiToken.value] : undefined);
        }
    }

    // Reconnection is owned entirely by this component (backoff, max attempts,
    // countdown UI). SocketClient auto-reconnect is disabled to prevent it from
    // firing additional attempts after we've declared reconnect_failed.
    socketClient.value = new SocketClient(ws_url, {
        reconnectEnabled: false,
        subprotocols,
    });

    socketClient.value.onOpen = () => {
        const wasReconnecting = session.phase.value.stage === "reconnecting";
        clearCountdownInterval();
        // Hard-reset audio on reconnect: cancel any currently-playing sources
        // and clear the queue so stale chunks from the previous OpenAI session
        // don't bleed into the new one.
        if (wasReconnecting) {
            stopPlayback();
            audioQueue.value = [];
        }
        // Reset session — preserve transcripts on reconnect so they don't vanish
        session.reset(wasReconnecting);
        error.value = null;
        if (wasReconnecting) {
            showReconnected.value = true;
            reconnectAttempts.value = 0;
            setTimeout(() => {
                showReconnected.value = false;
            }, CONFIG.timing.reconnectedToastMs);
        }
    };

    socketClient.value.onClose = () => {
        if (userEndedSession.value) {
            // User intentionally ended — terminal close, full teardown
            endChat();
            session.setEnded("user_ended");
            return;
        }
        // Any other close (server restart 1012, going-away 1001, network drop)
        // should enter the reconnect flow — not a dead-end error message.
        stopPlayback();
        detectInstability();
        reconnectAttempts.value++;

        // Check if we've exceeded max attempts
        if (reconnectAttempts.value > CONFIG.reconnection.maxAttempts) {
            session.setReconnectFailed();
            error.value = null;
            return;
        }

        const backoffMs = computeBackoffMs(reconnectAttempts.value);
        session.setReconnecting(reconnectAttempts.value);
        error.value = null;

        // Start countdown for next attempt. We own all reconnection (SocketClient
        // reconnectEnabled is false) — schedule a fresh connect after backoff.
        startReconnectCountdown(backoffMs);

        if (socketClient.value) {
            delete socketClient.value.instance;
            setTimeout(async () => {
                // Refresh the JWT before reconnecting so a token that expired during the
                // disconnect doesn't doom every retry with a 1008 auth rejection.
                if (coach_api_realtime_enabled) {
                    try {
                        await jwtToken.getToken();
                    } catch {
                        // connect() will surface any resulting handshake error.
                    }
                }
                socketClient.value?.connect();
            }, backoffMs);
        }
    };

    socketClient.value.onError = createWebSocketErrorHandler({ cs_id }, "RealtimeChat", () => {
        error.value = CONFIG.errorText.connectionError;
    });

    // Deferred "listening" event — held until audio playback finishes so "Your turn"
    // text doesn't appear while Nadia is still speaking through the jitter buffer.
    let pendingListeningEvent = null;

    socketClient.value.onMessage = (msg) => {
        try {
            const data = JSON.parse(msg.data);

            // Guardrail violation: route to the non-blocking toast (spoken-redirect, session
            // continues) or the session-ending "can't continue" modal. The modal must take
            // priority — it can't be blocked by a toast that's still on screen (a customized
            // toast is persistent and would otherwise block it indefinitely).
            if (data.type === "guardrail_triggered") {
                const guardrailText = data.display_message || "I'm not able to help with that in this coaching context.";
                if (data.spoken_redirect) {
                    // Session keeps going — Nadia spoke the redirect. Dedupe against any modal/toast
                    // already showing so we don't stack a toast on top of an active notification.
                    if (showGuardrailModal.value || showGuardrailToast.value) {
                        return;
                    }
                    guardrailMessage.value = guardrailText;
                    guardrailSpokenRedirect.value = true;
                    // Barge-in with a spoken redirect: flush the cut-off response's buffered audio
                    // so the steered redirect plays promptly instead of after the original finishes,
                    // and drop the cancelled response's still-in-flight deltas (see audio handler).
                    if (data.cut_audio) {
                        guardrailCutResponseId = data.cut_response_id || null;
                        stopPlayback();
                        audioQueue.value = [];
                    }
                    // Only clickable when it's an org customization AND there's a message to show
                    // in the modal — a customized guardrail with no static_response has nothing to
                    // reveal, so it falls back to the info-only, auto-dismissing toast.
                    guardrailToastCustomized.value = !!data.customized && !!data.display_message;
                    showGuardrailToast.value = true;
                    if (!guardrailToastCustomized.value) {
                        // Info-only toast: auto-dismiss after 8s.
                        guardrailToastTimer = setTimeout(() => {
                            showGuardrailToast.value = false;
                            guardrailToastTimer = null;
                        }, 8000);
                    }
                } else {
                    // Session-ending modal ("This conversation can't continue"): takes priority,
                    // so dismiss any active toast first, then show it. Dedupe only against a modal
                    // that's already showing.
                    dismissGuardrailToast();
                    if (showGuardrailModal.value) {
                        return;
                    }
                    guardrailMessage.value = guardrailText;
                    guardrailSpokenRedirect.value = false;
                    stopPlayback();
                    audioQueue.value = [];
                    pendingListeningEvent = null;
                    showGuardrailModal.value = true;
                }
                return;
            }

            // Defer the "listening" state event until audio finishes playing.
            // The server sends "listening" when the response is done *generating*,
            // but the jitter buffer still has queued audio to play through.
            if (data.type === "session_state" && data.state === "listening") {
                isProcessingResponse.value = false;

                // During an interruption, OpenAI cancels the response and fires
                // RESPONSE_DONE → "listening" while the user is still speaking.
                // Drop it — the user_speaking → processing_turn → thinking flow
                // handles the transition. Without this guard the status text
                // briefly flashes "Your turn" mid-speech.
                const p = session.phase.value;
                if (p.stage === "active" && (p.phase === "user_speaking" || p.phase === "processing_turn")) {
                    return;
                }

                scheduleNextChunks({ force: true });
                if (isPlaying.value) {
                    pendingListeningEvent = data;
                } else {
                    session.onEvent(data);
                }
                return;
            }

            // While the guardrail modal is open, pause the session — drop session_state
            // transitions so Nadia can't start a new turn before the user acts.
            // error and session_reconnected are still let through so the session stays
            // recoverable if the WS drops.
            // When spoken_redirect is active, session_state must flow through — Nadia is
            // already handling the redirect in voice and the session continues naturally.
            if (!guardrailSpokenRedirect.value && showGuardrailModal.value && data.type === "session_state") {
                return;
            }

            // When interruptions are off and Nadia is still speaking, suppress
            // the user_speaking state transition. The user's mic audio still flows
            // to the backend (it queues as next turn), but the UI stays in "speaking"
            // state so the user doesn't see a confusing "Listening…" while hearing Nadia.
            if (data.type === "session_state" && data.state === "user_speaking" && !session.currentSettings.value.allowInterruptions && isPlaying.value) {
                return;
            }

            if (data.type === "session_state" && data.state === "user_speaking") {
                showDisclaimer.value = false;
            }

            // Feed every event through the session state machine
            const result = session.onEvent(data);

            // Handle audio playback (returned from audio_delta events).
            // Drop audio while the guardrail modal is showing — the cancelled response
            // may have in-flight audio_delta chunks that arrive after guardrail_triggered,
            // and re-queuing them would restart playback despite stopPlayback() + clear.
            // When spoken_redirect is active, audio must flow through (that's Nadia speaking
            // the redirect) so the guard is relaxed — except for a response we barged in on,
            // whose still-in-flight deltas we drop so they don't refill the flushed buffer.
            const isCutResponse = guardrailCutResponseId !== null && result.responseId === guardrailCutResponseId;
            if (result.audioDelta && (!showGuardrailModal.value || guardrailSpokenRedirect.value) && !isCutResponse && !isOutputMuted.value) {
                showDisclaimer.value = false;
                try {
                    audioQueue.value.push({ delta: result.audioDelta, response_id: result.responseId });
                    scheduleNextChunks();
                } catch (e) {
                    logError(`Error processing audio delta: ${e}`);
                    isPlaying.value = false;
                }
            }

            // Handle max duration
            if (result.maxDuration) {
                handleSessionOverMaximumDuration();
                stopTimer();
            }

            // Audio side effects live here (not in the composable) because
            // useRealtimeSession is pure state — it doesn't know about AudioContext.
            if (data.type === "session_state") {
                if (data.state === "user_speaking") {
                    // Only stop playback when interruptions are enabled.
                    // When off, Nadia finishes her thought and the user's
                    // speech becomes the next turn (no local audio kill).
                    if (session.currentSettings.value.allowInterruptions) {
                        stopPlayback();
                        // stopPlayback() re-queues unplayed chunks (for false VAD
                        // triggers). On a real interruption we don't want them —
                        // discard so stale Nadia audio doesn't replay.
                        audioQueue.value = [];
                    }
                    // Cancel deferred listening event — user spoke before audio finished
                    pendingListeningEvent = null;
                }
                if (data.state === "thinking") {
                    // response.created fired — flush the queue so re-queued chunks
                    // from a real interruption don't play before the new response.
                    audioQueue.value = [];
                    isProcessingResponse.value = true;
                    // Cancel any stale deferred listening
                    pendingListeningEvent = null;
                }
            }

            // Handle reconnection (clear stale audio)
            if (data.type === "session_reconnected") {
                endStartupMicLock();
                showDisclaimer.value = false;
                audioQueue.value = [];
                error.value = null;
                showReconnected.value = true;
                reconnectAttempts.value = 0;
                setTimeout(() => {
                    showReconnected.value = false;
                }, CONFIG.timing.reconnectedToastMs);
            }

            // Surface errors
            if (data.type === "error") {
                error.value = data.message;
            }
        } catch (e) {
            logError(`Error parsing WebSocket message: ${e}`);
        }
    };

    socketClient.value.connect();

    // When audio playback finishes, fire any deferred "listening" event so the
    // session state machine transitions to listening (shows "Your turn" text)
    // only after the user can actually hear that Nadia is done.
    watch(isPlaying, (playing) => {
        if (!playing && pendingListeningEvent) {
            session.onEvent(pendingListeningEvent);
            pendingListeningEvent = null;
        }
    });
};

// Guardrail modal action handlers — close the modal first (mirrors handleSessionEndDialogClose
// which is the working pattern). Leaving the dialog open while triggering Inertia navigation
// interferes with reka-ui's focus trap and pointer-event management.
const handleGuardrailEnd = () => {
    showGuardrailModal.value = false;
    handleEndRolePlay();
};

const handleGuardrailRestart = async () => {
    if (!practice_again_params) {
        return;
    }
    showGuardrailModal.value = false;
    endStartupMicLock({ restoreMic: false });
    userEndedSession.value = true;

    // Stop mic and close WS before navigating. onUnmounted's endChat() is async
    // and would race against the new session's startChat/mic initialization if we
    // let Inertia trigger it. Doing it here ensures a clean teardown first.
    await endChat();
    socketClient.value?.removeListeners();
    socketClient.value?.instance?.close();

    // Finish the current role play on the backend (fire-and-forget, no navigation).
    // This mirrors what handleEndRolePlay does via router.put but without navigating
    // to the summary page — the user goes straight to the new session instead.
    useFetch(`/role-play/summary/${role_play.hashid}/`, { immediate: false }).put().execute();

    router.post("/coach/realtime_chat/", practice_again_params, {
        headers: { "X-CSRFToken": getCookie("csrftoken") },
    });
};

const handleEndRolePlay = () => {
    endStartupMicLock({ restoreMic: false });
    userEndedSession.value = true;
    router.put(
        `/role-play/summary/${role_play.hashid}/`,
        {},
        {
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
            },
            onSuccess: () => {
                logUserInteraction("role_play_summary_sent", { id: role_play.hashid }, cs_uuid);
            },
        },
    );
};

const handleSessionEndDialogClose = () => {
    showSessionEndDialog.value = false;
    handleEndRolePlay();
};

// Clean up on component unmount
onUnmounted(async () => {
    clearCountdownInterval();
    endStartupMicLock({ restoreMic: false });
    clearGuardrailToastTimer();
    await endChat();
    socketClient.value.removeListeners();
    socketClient.value?.instance?.close();
    socketClient.value = null;
    stopTimer();
});
</script>

<style scoped>
.background {
    background-color: #cfc5f2;
    background-image: radial-gradient(20% 60% at 100% 10%, #f5d8e2, transparent), radial-gradient(60% 20% at 20% -10%, #f5d8e2, transparent),
        radial-gradient(30% 30% at -10% 70%, #f5d8e2, transparent);
}

.slide-down-leave-active,
.slide-down-enter-active {
    transition: all 300ms ease-out;
}
.slide-down-leave-from,
.slide-down-enter-to {
    transform: translateY(0);
    opacity: 1;
}
.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(-10%);
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 400ms ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-up-leave-active,
.slide-up-enter-active {
    transition: all 300ms ease-out;
}
.slide-up-leave-from,
.slide-up-enter-to {
    transform: translateY(0);
    opacity: 1;
}
.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(10%);
    opacity: 0;
}
</style>
